import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../hooks/useLanguage';
import Section from '../ui/Section';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import { getProjects } from '../../services/api';

const Portfolio = () => {
    const { t, language } = useLanguage();
    const [projects, setProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchProjects = async () => {
            try {
                const data = await getProjects();
                setProjects(data);
            } catch (error) {
                console.error("Failed to fetch projects:", error);
                // Fallback or empty state could be handled here
            } finally {
                setLoading(false);
            }
        };

        fetchProjects();
    }, []);

    const getLocalized = (field) => {
        if (typeof field === 'object' && field !== null) {
            return field[language] || field['en'] || '';
        }
        return field;
    };

    return (
        <Section id="projects" background="white">
            <div className="text-center max-w-3xl mx-auto mb-16">
                <h2 className="text-3xl md:text-4xl font-bold mb-4 text-slate-900">{t.portfolio.title}</h2>
                <p className="text-slate-600 text-lg">
                    {t.portfolio.description}
                </p>
            </div>

            {loading ? (
                <div className="text-center py-12">
                    <p className="text-slate-500">{t.portfolio.loading}</p>
                </div>
            ) : (
                <div className="grid md:grid-cols-2 gap-8">
                    {/* Show only first 4 projects on Home */}
                    {projects.slice(0, 4).map((project, index) => (
                        <Card
                            key={project.id || index}
                            className="group cursor-pointer"
                            onClick={() => setSelectedProject(project)}
                        >
                            <div className="relative h-64 overflow-hidden">
                                <img
                                    src={project.image}
                                    alt={getLocalized(project.title)}
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                />
                                <div className="absolute inset-0 bg-slate-900/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                                    <Button variant="outline" className="text-white border-white hover:bg-white hover:text-slate-900">
                                        {t.portfolio.viewDetails}
                                    </Button>
                                </div>
                            </div>
                            <div className="p-6">
                                <span className="text-gold-600 font-semibold text-sm tracking-wider uppercase mb-2 block">
                                    {getLocalized(project.category)}
                                </span>
                                <h3 className="text-2xl font-bold mb-3 text-slate-900 group-hover:text-gold-600 transition-colors">
                                    {getLocalized(project.title)}
                                </h3>
                                <p className="text-slate-600 line-clamp-3">
                                    {getLocalized(project.description)}
                                </p>
                            </div>
                        </Card>
                    ))}
                </div>
            )}

            <div className="text-center mt-12">
                <a href="/projects">
                    <Button variant="secondary">{t.portfolio.viewAll}</Button>
                </a>
            </div>

            {/* Project Details Modal */}
            {selectedProject && (
                <Modal isOpen={!!selectedProject} onClose={() => setSelectedProject(null)}>
                    <div className="relative h-64 md:h-80">
                        <img
                            src={selectedProject.image}
                            alt="Project Loading"
                            className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 to-transparent flex items-end p-8">
                            <div className="w-full">
                                <span className="text-gold-500 font-bold tracking-wider uppercase mb-2 block text-sm">
                                    {getLocalized(selectedProject.category)}
                                </span>
                                <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
                                    {getLocalized(selectedProject.title)}
                                </h3>
                            </div>
                        </div>
                    </div>
                    <div className="p-8">
                        <div className="mb-8">
                            <h4 className="text-xl font-bold text-slate-900 mb-4">{t.portfolio.overview}</h4>
                            <p className="text-slate-600 leading-relaxed text-lg line-clamp-4">
                                {getLocalized(selectedProject.description)}
                            </p>
                        </div>

                        <div className="flex justify-between items-center pt-4 border-t border-slate-100">
                            <div className="space-y-1">
                                <p className="text-sm text-slate-500">Status</p>
                                <p className="font-semibold text-green-600">
                                    {getLocalized(selectedProject.status) || 'Completed'}
                                </p>
                            </div>

                            <a
                                href={`/project/${selectedProject.id}`}
                                className="bg-slate-900 text-white px-6 py-3 rounded-lg font-semibold hover:bg-gold-600 transition-colors inline-block"
                            >
                                {t.portfolio.viewDetails}
                            </a>
                        </div>
                    </div>
                </Modal>
            )}
        </Section>
    );
};

export default Portfolio;
